import type { JsonQuestion } from '@/components/questionnaires/json-driven/types';
import { isFamilyHistoryMedicalValue, isPcosWorkupReference } from './medicalValueContext';
import { questionHasMongoTriggerMapping } from './triggerConfigFromSection';
import type { TriggerRule, TriggerRuleConfig, TriggerConditionContext } from './types';

/**
 * Normalize raw answer to string array (select → single, multi-select → many).
 */
export function selectedValuesFromAnswer(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((v): v is string => typeof v === 'string' && v.length > 0);
  }
  if (typeof value === 'boolean') {
    return [value ? 'true' : 'false'];
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    return [value.trim()];
  }
  return [];
}

function normalizeGender(value: unknown): TriggerConditionContext['gender'] {
  if (typeof value !== 'string') return undefined;
  const v = value.trim().toLowerCase().replace(/[_-]+/g, ' ');
  if (!v) return undefined;

  // Check female first so strings like "female" are never misread as "male".
  if (
    /\bfemale\b/.test(v) ||
    /\bwomen?\b/.test(v) ||
    /\bgirl\b/.test(v) ||
    /\bf\b/.test(v) ||
    /assigned female at birth/.test(v)
  ) {
    return 'female';
  }
  if (
    /\bmale\b/.test(v) ||
    /\bmen?\b/.test(v) ||
    /\bboy\b/.test(v) ||
    /\bm\b/.test(v) ||
    /assigned male at birth/.test(v)
  ) {
    return 'male';
  }
  return undefined;
}

export function parseGenderFromUnknown(value: unknown): TriggerConditionContext['gender'] {
  if (Array.isArray(value)) {
    for (const item of value) {
      const parsed = normalizeGender(item);
      if (parsed) return parsed;
    }
    return undefined;
  }
  return normalizeGender(value);
}

/** Parse gender from a stored answer, resolving select/radio option values via labels when needed. */
export function parseGenderFromAnswer(
  question: JsonQuestion | undefined,
  value: unknown
): TriggerConditionContext['gender'] {
  for (const raw of selectedValuesFromAnswer(value)) {
    const direct = parseGenderFromUnknown(raw);
    if (direct) return direct;

    if (!question?.options?.length) continue;
    for (const opt of question.options) {
      if (opt.value !== raw && opt.label !== raw) continue;
      const fromOpt =
        parseGenderFromUnknown(opt.value) ?? parseGenderFromUnknown(opt.label);
      if (fromOpt) return fromOpt;
    }
  }
  return undefined;
}

function ageFromDob(value: string): number | undefined {
  // Accept ISO-like DOB values from date inputs.
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return undefined;
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  if (!Number.isFinite(year) || !Number.isFinite(monthIndex) || !Number.isFinite(day)) return undefined;
  const dob = new Date(Date.UTC(year, monthIndex, day));
  if (
    Number.isNaN(dob.getTime()) ||
    dob.getUTCFullYear() !== year ||
    dob.getUTCMonth() !== monthIndex ||
    dob.getUTCDate() !== day
  ) {
    return undefined;
  }
  const now = new Date();
  let age = now.getUTCFullYear() - year;
  const m = now.getUTCMonth() - monthIndex;
  if (m < 0 || (m === 0 && now.getUTCDate() < day)) age -= 1;
  return age >= 0 ? age : undefined;
}

function ageFromBirthYear(year: number): number | undefined {
  if (!Number.isFinite(year)) return undefined;
  const y = Math.trunc(year);
  const now = new Date();
  const currentYear = now.getFullYear();
  // Treat plausible calendar years as DOB year (e.g. 2009 -> 17).
  if (y < currentYear - 120 || y > currentYear) return undefined;
  return currentYear - y;
}

function parseAge(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    const fromBirthYear = ageFromBirthYear(value);
    if (fromBirthYear != null) return fromBirthYear;
    return value <= 130 ? value : undefined;
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const iso = value.toISOString().slice(0, 10);
    return ageFromDob(iso);
  }

  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  const fromDob = ageFromDob(trimmed);
  if (fromDob != null) return fromDob;

  // Handles year-only values like "2009" from year pickers/selects.
  if (/^\d{4}$/.test(trimmed)) {
    const fromBirthYear = ageFromBirthYear(Number(trimmed));
    if (fromBirthYear != null) return fromBirthYear;
  }

  // Handles values like "17", "17.5", "17 years", "17 yrs".
  const match = trimmed.match(/^(\d+(?:\.\d+)?)(?:\s*[a-zA-Z]+(?:\s+[a-zA-Z]+)*)?$/);
  if (!match) return undefined;
  const n = Number(match[1]);
  if (!Number.isFinite(n) || n < 0) return undefined;
  const fromBirthYear = ageFromBirthYear(n);
  if (fromBirthYear != null) return fromBirthYear;
  return n <= 130 ? n : undefined;
}

function parsePregnancy(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') {
    if (value === 1) return true;
    if (value === 0) return false;
    return undefined;
  }
  if (typeof value !== 'string') return undefined;

  const v = value
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
  if (!v) return undefined;

  const negative = new Set([
    'none',
    'not pregnant',
    'non pregnant',
    'no',
    'n',
    'false',
    '0',
  ]);
  if (
    negative.has(v) ||
    /\bnot\s+(currently\s+)?pregnant\b/.test(v) ||
    /\bcurrently\s+not\s+pregnant\b/.test(v) ||
    /\bnon\s+pregnant\b/.test(v)
  ) {
    return false;
  }

  const positive = new Set([
    'pregnant',
    'pregnancy',
    'yes',
    'y',
    'true',
    '1',
    'first',
    'second',
    'third',
    'first trimester',
    'second trimester',
    'third trimester',
  ]);
  if (positive.has(v)) return true;
  if (v.includes('pregnant') && !v.includes('not pregnant') && !v.includes('non pregnant')) {
    return true;
  }
  if (
    /\b([123](st|nd|rd)|first|second|third)\s+trimester\b/.test(v) ||
    /\btrimester\s*([123]|one|two|three)\b/.test(v)
  ) {
    return true;
  }

  return undefined;
}

export function parsePregnancyFromUnknown(value: unknown): boolean | undefined {
  if (Array.isArray(value)) {
    let sawFalse = false;
    for (const item of value) {
      const parsed = parsePregnancy(item);
      if (parsed === true) return true;
      if (parsed === false) sawFalse = true;
    }
    return sawFalse ? false : undefined;
  }
  return parsePregnancy(value);
}

function normalizeForMatch(value: string): string {
  return value
    .toLowerCase()
    .replace(/[’']/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function normalizeForExactTriggerMatch(value: string): string {
  return value
    .toLowerCase()
    .replace(/[’']/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function valuesMatchForTriggerQuestion(optionValue: string, triggerValue: string): boolean {
  const optionNormalized = normalizeForExactTriggerMatch(optionValue);
  const triggerNormalized = normalizeForExactTriggerMatch(triggerValue);
  if (!optionNormalized || !triggerNormalized) return false;
  if (optionNormalized === triggerNormalized) return true;

  // Allow safe phrase containment so values like "Type 1 Diabetes"
  // map to trigger "Type 1", while avoiding broad semantic matching.
  if (optionNormalized.length < 3 || triggerNormalized.length < 3) return false;
  const optionWithBoundaries = ` ${optionNormalized} `;
  const triggerWithBoundaries = ` ${triggerNormalized} `;
  return (
    optionWithBoundaries.includes(` ${triggerNormalized} `) ||
    triggerWithBoundaries.includes(` ${optionNormalized} `)
  );
}

function canonicalMedicalTag(value: string): string | undefined {
  const n = normalizeForMatch(value);
  if (!n) return undefined;
  if (isFamilyHistoryMedicalValue(value)) return undefined;
  if (isPcosWorkupReference(value)) return undefined;

  if (
    /\bgestational\b/.test(n) ||
    /\bgdm\b/.test(n)
  ) {
    return 'diabetes_gestational';
  }
  if (
    /\blada\b/.test(n) ||
    /\blatent autoimmune diabetes/.test(n)
  ) {
    return 'diabetes_lada';
  }
  if (
    /\btype\s*1\b/.test(n) ||
    /\btype\s*i\b/.test(n) ||
    /\bt1d\b/.test(n) ||
    /\bt1dm\b/.test(n)
  ) {
    return 'diabetes_type_1';
  }
  if (
    /\btype\s*2\b/.test(n) ||
    /\btype\s*ii\b/.test(n) ||
    /\bt2d\b/.test(n) ||
    /\bt2dm\b/.test(n)
  ) {
    return 'diabetes_type_2';
  }
  // Plain "diabetes" (no type qualifier) — must be checked AFTER type-specific
  // patterns so "Type 1 Diabetes" is not caught here first.
  if (/^diabetes$/.test(n) || n === 'dm') {
    return 'diabetes_unspecified';
  }
  if (
    /\binsulin\s+resistance\b/.test(n) ||
    /\binsulin\s+resistant\b/.test(n) ||
    n === 'ir'
  ) {
    return 'insulin_resistance';
  }
  if (
    /\bpcos\b/.test(n) ||
    /\bpolycystic ovary\b/.test(n)
  ) {
    return 'pcos';
  }
  if (
    /\bhashimoto/.test(n)
  ) {
    return 'thyroid_hashimotos';
  }
  if (/\bgraves\b/.test(n)) {
    return 'thyroid_graves';
  }
  if (
    /\bhyperthyroid/.test(n) ||
    /\boveractive\b/.test(n)
  ) {
    return 'thyroid_hyper';
  }
  if (
    /\bhypothyroid/.test(n) ||
    /\bunderactive\b/.test(n)
  ) {
    return 'thyroid_hypo';
  }
  // Plain "thyroid" (no subtype qualifier) — must be checked AFTER all subtype-specific
  // patterns so "Hashimoto's", "Hyperthyroid", "Hypothyroid" are not caught here first.
  if (/^thyroid$/.test(n)) {
    return 'thyroid_unspecified';
  }
  return undefined;
}

export function valuesSemanticallyMatch(selectedValue: string, triggerValue: string): boolean {
  if (
    isFamilyHistoryMedicalValue(selectedValue) ||
    isFamilyHistoryMedicalValue(triggerValue) ||
    isPcosWorkupReference(selectedValue) ||
    isPcosWorkupReference(triggerValue)
  ) {
    return false;
  }

  const selectedCanonical = canonicalMedicalTag(selectedValue);
  const triggerCanonical = canonicalMedicalTag(triggerValue);
  // If either side has a canonical tag, require both to have the same canonical tag.
  // This prevents plain "Diabetes" (diabetes_unspecified) from matching
  // type-specific trigger values like "Type 1" (diabetes_type_1).
  if (selectedCanonical || triggerCanonical) {
    return selectedCanonical === triggerCanonical;
  }

  const s = normalizeForMatch(selectedValue);
  const t = normalizeForMatch(triggerValue);
  if (!s || !t) return false;
  if (s === t) return true;
  // Containment fallback for slight text differences (e.g. "type 1 diabetes" vs "type 1").
  // Require a minimum length to avoid short strings matching unrelated longer ones.
  if (s.length < 3 || t.length < 3) return false;
  return s.includes(t) || t.includes(s);
}

function toKeyArray(value?: string | string[]): string[] {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

function getAnswerByKeyInsensitive(
  answers: Record<string, unknown>,
  key: string
): unknown {
  if (answers[key] != null) return answers[key];
  const normalized = key.trim().toLowerCase();
  for (const [k, v] of Object.entries(answers)) {
    if (k.trim().toLowerCase() === normalized) return v;
  }
  return undefined;
}

function normalizeKeyForSemanticMatch(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .toLowerCase()
    .trim();
}

function isHistoricalAgeContext(normalized: string): boolean {
  return (
    /\bdiagnos/.test(normalized) ||
    /\bonset\b/.test(normalized) ||
    /\bfirst symptom\b/.test(normalized) ||
    /\bstarted\b/.test(normalized) ||
    /\bbegan\b/.test(normalized)
  );
}

export function looksLikeAgeContextField(value: string): boolean {
  const normalized = normalizeKeyForSemanticMatch(value);
  if (!normalized || isHistoricalAgeContext(normalized)) return false;

  return (
    normalized === 'age' ||
    normalized === 'your age' ||
    normalized === 'client age' ||
    normalized === 'current age' ||
    normalized === 'age years' ||
    normalized === 'age in years' ||
    /\bhow old are you\b/.test(normalized) ||
    /\bdob\b/.test(normalized) ||
    /\bdate of birth\b/.test(normalized) ||
    /\byear of birth\b/.test(normalized) ||
    /\bbirth year\b/.test(normalized) ||
    /\bbirth date\b/.test(normalized) ||
    /\bbirthdate\b/.test(normalized)
  );
}

function keyLooksLikeGender(key: string): boolean {
  const normalized = normalizeKeyForSemanticMatch(key);
  if (!normalized) return false;
  return (
    /\bgender\b/.test(normalized) ||
    /\bsex\b/.test(normalized) ||
    /\bbiological sex\b/.test(normalized) ||
    /\bsex assigned at birth\b/.test(normalized) ||
    /\bsex at birth\b/.test(normalized)
  );
}

function keyLooksLikeAge(key: string): boolean {
  return looksLikeAgeContextField(key);
}

function keyLooksLikePregnancy(key: string): boolean {
  const normalized = normalizeKeyForSemanticMatch(key);
  if (!normalized) return false;
  return (
    /\bpregnan/.test(normalized) ||
    /\btrimester\b/.test(normalized) ||
    /\bgestat/.test(normalized)
  );
}

function pickFirstParsed<T>(
  answers: Record<string, unknown>,
  keys: string[],
  parser: (value: unknown) => T | undefined
): T | undefined {
  for (const key of keys) {
    const raw = getAnswerByKeyInsensitive(answers, key);
    if (raw == null) continue;
    const parsed = parser(raw);
    if (parsed != null) return parsed;
  }
  return undefined;
}

function conditionsMatch(
  rule: TriggerRule,
  ctx: TriggerConditionContext,
  _selectedValues: string[]
): boolean {
  const c = rule.conditions;
  if (!c) return true;

  if (c.gender != null && ctx.gender !== c.gender) return false;
  if (c.ageMin != null && (ctx.age == null || ctx.age < c.ageMin)) return false;
  if (c.ageMax != null && (ctx.age == null || ctx.age > c.ageMax)) return false;
  if (c.pregnant != null && ctx.pregnant !== c.pregnant) return false;
  return true;
}

/**
 * Fallback matching for assigned clients whose share payload may not
 * include every demographic field yet. Known context still rejects contradictions;
 * missing gender/age can be handled later by safest-candidate selection. Pregnancy
 * rules still require explicit pregnancy context.
 */
function conditionsCompatibleWithKnownContext(
  rule: TriggerRule,
  ctx: TriggerConditionContext,
  _selectedValues: string[]
): boolean {
  const c = rule.conditions;
  if (!c) return true;

  if (c.gender != null && ctx.gender != null && ctx.gender !== c.gender) return false;
  if (c.ageMin != null && ctx.age != null && ctx.age < c.ageMin) return false;
  if (c.ageMax != null && ctx.age != null && ctx.age > c.ageMax) return false;
  if (c.pregnant != null && ctx.pregnant !== c.pregnant) return false;
  return true;
}

function ruleHasTrigger(rule: TriggerRule, selectedValues: string[]): boolean {
  return rule.triggerValues.some((triggerValue) =>
    selectedValues.some((selectedValue) =>
      valuesSemanticallyMatch(selectedValue, triggerValue)
    )
  );
}

function dedupeAndSortRules(rules: TriggerRule[]): TriggerRule[] {
  const seen = new Set<string>();
  const out: TriggerRule[] = [];
  for (const rule of rules) {
    if (seen.has(rule.questionnaireId)) continue;
    seen.add(rule.questionnaireId);
    out.push(rule);
  }
  return out.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
}

function preferBestFallbackRules(
  rules: TriggerRule[],
  context: TriggerConditionContext
): TriggerRule[] {
  let candidates = dedupeAndSortRules(rules);

  if (context.gender != null) {
    const genderScoped = candidates.filter((rule) => rule.conditions?.gender === context.gender);
    if (genderScoped.length > 0) candidates = genderScoped;
  }

  // If age is missing, prefer adult/unbounded rules over child/teen rules.
  if (context.age == null) {
    const adultOrUnbounded = candidates.filter((rule) => rule.conditions?.ageMax == null);
    if (adultOrUnbounded.length > 0) candidates = adultOrUnbounded;
  }

  // Without gender, do not guess between male and female questionnaire variants.
  if (context.gender == null) {
    const scopedGenders = new Set(
      candidates
        .map((rule) => rule.conditions?.gender)
        .filter((gender): gender is 'male' | 'female' => gender === 'male' || gender === 'female')
    );
    if (scopedGenders.size > 1) return [];
  }

  return candidates;
}

/**
 * Evaluate trigger rules: given selected values and context, return matching rules.
 * Deduplicated by questionnaireId (first match wins), ordered by rule order.
 */
export function evaluateTriggerRules(
  selectedValues: string[],
  context: TriggerConditionContext,
  config: TriggerRuleConfig
): TriggerRule[] {
  const strictlyMatched: TriggerRule[] = [];
  const fallbackMatched: TriggerRule[] = [];

  for (const rule of config.rules) {
    if (!ruleHasTrigger(rule, selectedValues)) continue;
    if (conditionsMatch(rule, context, selectedValues)) {
      strictlyMatched.push(rule);
      continue;
    }
    if (conditionsCompatibleWithKnownContext(rule, context, selectedValues)) {
      fallbackMatched.push(rule);
    }
  }

  const matched = dedupeAndSortRules(strictlyMatched);

  if (matched.length > 0) return matched;

  return preferBestFallbackRules(fallbackMatched, context);
}

/**
 * Build context from answers using contextFields mapping.
 * Values are normalized (e.g. gender from select options, age from number).
 */
export function buildContextFromAnswers(
  answers: Record<string, unknown>,
  contextFields: TriggerRuleConfig['contextFields']
): TriggerConditionContext {
  const ctx: TriggerConditionContext = {};
  if (!contextFields) return ctx;

  const gKeys = toKeyArray(contextFields.gender);
  const gender = pickFirstParsed(answers, gKeys, parseGenderFromUnknown);
  if (gender != null) {
    ctx.gender = gender;
  } else {
    for (const [key, raw] of Object.entries(answers)) {
      if (!keyLooksLikeGender(key)) continue;
      const parsed = parseGenderFromUnknown(raw);
      if (parsed) {
        ctx.gender = parsed;
        break;
      }
    }

    // Final fallback for ID-based keys where key name gives no hint.
    if (ctx.gender == null) {
      const inferred = new Set<TriggerConditionContext['gender']>();
      for (const raw of Object.values(answers)) {
        const parsed = parseGenderFromUnknown(raw);
        if (parsed) inferred.add(parsed);
      }
      if (inferred.size === 1) {
        ctx.gender = Array.from(inferred)[0];
      }
    }
  }

  const aKeys = toKeyArray(contextFields.age);
  const age = pickFirstParsed(answers, aKeys, parseAge);
  if (age != null) {
    ctx.age = age;
  } else {
    for (const [key, raw] of Object.entries(answers)) {
      if (!keyLooksLikeAge(key)) continue;
      const parsed = parseAge(raw);
      if (parsed != null) {
        ctx.age = parsed;
        break;
      }
    }
  }

  const pKeys = toKeyArray(contextFields.pregnancy);
  const pregnant = pickFirstParsed(answers, pKeys, parsePregnancyFromUnknown);
  if (pregnant != null) {
    ctx.pregnant = pregnant;
  } else {
    for (const [key, raw] of Object.entries(answers)) {
      if (!keyLooksLikePregnancy(key)) continue;
      const parsed = parsePregnancyFromUnknown(raw);
      if (parsed != null) {
        ctx.pregnant = parsed;
        break;
      }
    }
  }

  return ctx;
}

const TRIGGER_QUESTION_TYPES = ['select', 'multi-select', 'radio'] as const;

/**
 * All trigger values from config (option values that can fire rules).
 */
export function allTriggerValues(config: TriggerRuleConfig): Set<string> {
  const s = new Set<string>();
  for (const r of config.rules) {
    for (const v of r.triggerValues) s.add(v);
  }
  return s;
}

/**
 * True if this question is a trigger field: select/multi-select/radio and
 * at least one option value appears in any rule's triggerValues.
 */
function optionMatchesAnyTriggerValue(
  question: JsonQuestion,
  option: { value: string; label: string },
  triggerValues: string[]
): boolean {
  if (
    isFamilyHistoryMedicalValue(option.value) ||
    isFamilyHistoryMedicalValue(option.label) ||
    isPcosWorkupReference(option.value) ||
    isPcosWorkupReference(option.label)
  ) {
    return false;
  }

  const allowSemanticMedicalMatch = questionLooksLikeMedicalTriggerContext(question);
  return triggerValues.some(
    (triggerValue) =>
      valuesMatchForTriggerQuestion(option.value, triggerValue) ||
      valuesMatchForTriggerQuestion(option.label, triggerValue) ||
      (allowSemanticMedicalMatch &&
        (valuesSemanticallyMatch(option.value, triggerValue) ||
          valuesSemanticallyMatch(option.label, triggerValue)))
  );
}

export function questionLooksLikeMedicalTriggerContext(question: JsonQuestion): boolean {
  const normalized = normalizeKeyForSemanticMatch(
    [question.questionText, question.label, question.externalId]
      .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
      .join(' ')
  );
  if (!normalized) return false;

  return (
    /\bwhich\s+type\b/.test(normalized) ||
    /\bwhat\s+type\b/.test(normalized) ||
    /\bdiagnos/.test(normalized) ||
    /\bcondition/.test(normalized) ||
    /\bmedical\b/.test(normalized) ||
    /\bdisorder/.test(normalized) ||
    /\bsubtype\b/.test(normalized)
  );
}

export function isTriggerQuestion(
  question: JsonQuestion,
  config: TriggerRuleConfig
): boolean {
  if (questionHasMongoTriggerMapping(question)) return true;
  if (!TRIGGER_QUESTION_TYPES.includes(question.type as (typeof TRIGGER_QUESTION_TYPES)[number])) {
    return false;
  }
  const opts = question.options ?? [];
  if (opts.length === 0) return false;

  const configuredTriggerValues = config.rules.flatMap((rule) => rule.triggerValues);
  return opts.some((o) => optionMatchesAnyTriggerValue(question, o, configuredTriggerValues));
}
